import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_LABELS = { pending:'Pending', in_progress:'In Progress', awaiting_info:'Awaiting Info', escalated:'Escalated', resolved:'Resolved', closed:'Closed' };
const PRIO_COLOR = { critical:'prio-critical', high:'prio-high', medium:'prio-medium', low:'prio-low' };
const VIEW_LABELS = { open:'Open Tickets', sla_breached:'SLA Breached', resolved_today:'Resolved Today' };

// Build initial filter state from the URL's query string, so links like
// /tickets?assigned_to_me=true or /tickets?view=sla_breached (from the
// Dashboard) actually filter the list instead of being silently ignored.
function filterFromSearch(search) {
  const params = new URLSearchParams(search);
  return {
    status: params.get('status') || '',
    priority: params.get('priority') || '',
    search: params.get('search') || '',
    date_from: '',
    date_to: '',
    page: 1,
    assigned_to_me: params.get('assigned_to_me') === 'true',
    view: params.get('view') || '',
  };
}

export default function TicketList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const isStaff = can('ict_staff','ict_manager','finance_officer','super_admin','branch_manager');

  const [tickets, setTickets]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState(() => filterFromSearch(location.search));
  const [selected, setSelected]     = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Re-sync filters whenever the URL's query string changes (e.g. clicking
  // a different Dashboard card while already on /tickets, which React
  // Router treats as the same route and won't remount this component).
  useEffect(() => { setFilter(filterFromSearch(location.search)); }, [location.search]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    try {
      const params = new URLSearchParams();
      if (filter.status)    params.append('status',    filter.status);
      if (filter.priority)  params.append('priority',  filter.priority);
      if (filter.search)    params.append('search',    filter.search);
      if (filter.date_from) params.append('date_from', filter.date_from);
      if (filter.date_to)   params.append('date_to',   filter.date_to);
      if (filter.assigned_to_me) params.append('assigned_to_me', 'true');
      if (filter.view)      params.append('view', filter.view);
      params.append('page',  filter.page);
      params.append('limit', 15);
      const res = await api.get(`/api/tickets?${params}`);
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const clearSpecialFilter = () => {
    setFilter(f => ({ ...f, assigned_to_me:false, view:'', page:1 }));
    navigate('/tickets', { replace:true });
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const toggleAll = () => {
    setSelected(s => s.length === tickets.length ? [] : tickets.map(t => t.id));
  };

  const applyBulkAction = async () => {
    if (!bulkAction) return toast.error('Please select an action');
    if (selected.length === 0) return toast.error('Please select at least one ticket');
    if (!window.confirm(`Apply "${STATUS_LABELS[bulkAction] || bulkAction}" to ${selected.length} ticket(s)?`)) return;
    setBulkLoading(true);
    try {
      await api.post('/api/tickets/bulk', { ids: selected, status: bulkAction });
      toast.success(`${selected.length} ticket(s) updated`);
      setSelected([]);
      setBulkAction('');
      fetchTickets();
    } catch (err) { toast.error(err.response?.data?.message || 'Bulk action failed'); }
    finally { setBulkLoading(false); }
  };

  const statusFilters = ['','pending','in_progress','awaiting_info','escalated','resolved','closed'];
  const statusLabels  = ['All','Pending','In Progress','Awaiting Info','Escalated','Resolved','Closed'];

  return (
    <div>
      <div className="section-head" style={{ marginBottom:14 }}>
        <div className="section-title">Tickets <span style={{ color:'var(--text3)', fontWeight:400, fontSize:13 }}>({total})</span></div>
        <button className="btn btn-orange btn-sm" onClick={() => navigate('/tickets/new')}>+ New Ticket</button>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <input className="form-input" style={{ maxWidth:280 }} placeholder="🔍  Search by subject, ticket number…"
          value={filter.search} onChange={e => setFilter({ ...filter, search:e.target.value, page:1 })} />
        <select className="form-select" style={{ width:140 }}
          value={filter.priority} onChange={e => setFilter({ ...filter, priority:e.target.value, page:1 })}>
          <option value="">All Priorities</option>
          <option value="critical">🔴 Critical</option>
          <option value="high">🟠 High</option>
          <option value="medium">🔵 Medium</option>
          <option value="low">⚪ Low</option>
        </select>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <label style={{ fontSize:11, color:'var(--text3)', whiteSpace:'nowrap' }}>From</label>
          <input className="form-input" type="date" style={{ width:145 }}
            value={filter.date_from} onChange={e => setFilter({ ...filter, date_from:e.target.value, page:1 })} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <label style={{ fontSize:11, color:'var(--text3)', whiteSpace:'nowrap' }}>To</label>
          <input className="form-input" type="date" style={{ width:145 }}
            value={filter.date_to} onChange={e => setFilter({ ...filter, date_to:e.target.value, page:1 })} />
        </div>
        {(filter.date_from || filter.date_to) && (
          <button className="btn btn-ghost btn-sm" style={{ alignSelf:'center' }}
            onClick={() => setFilter({ ...filter, date_from:'', date_to:'', page:1 })}>
            ✕ Clear dates
          </button>
        )}
      </div>

      <div className="filter-row" style={{ marginBottom:14 }}>
        {statusFilters.map((s,i) => (
          <span key={s} className={`chip${filter.status===s?' active':''}`}
            onClick={() => setFilter({ ...filter, status:s, page:1 })}>
            {statusLabels[i]}
          </span>
        ))}
      </div>

      {(filter.assigned_to_me || filter.view) && (
        <div style={{
          display:'flex', alignItems:'center', gap:10, padding:'8px 14px',
          background:'var(--sky-light, #eff6ff)', border:'1px solid var(--sky)',
          borderRadius:8, marginBottom:12, fontSize:12, color:'var(--sky-dark)', fontWeight:600,
        }}>
          Showing: {filter.assigned_to_me ? 'Assigned to Me' : VIEW_LABELS[filter.view] || filter.view}
          <button className="btn btn-ghost btn-sm" onClick={clearSpecialFilter}>✕ Clear</button>
        </div>
      )}

      {/* Bulk action bar — only for staff */}
      {isStaff && selected.length > 0 && (
        <div style={{
          display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
          background:'var(--sky-light, #eff6ff)', border:'1px solid var(--sky)',
          borderRadius:8, marginBottom:12, flexWrap:'wrap',
        }}>
          <span style={{ fontSize:13, fontWeight:600, color:'var(--sky-dark)' }}>
            {selected.length} ticket{selected.length > 1 ? 's' : ''} selected
          </span>
          <select className="form-select" style={{ width:180, fontSize:12, padding:'5px 8px' }}
            value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
            <option value="">Choose action…</option>
            <option value="in_progress">▶ Mark In Progress</option>
            <option value="awaiting_info">⏳ Mark Awaiting Info</option>
            <option value="resolved">✅ Mark Resolved</option>
            <option value="closed">🔒 Mark Closed</option>
          </select>
          <button className="btn btn-primary btn-sm" onClick={applyBulkAction} disabled={bulkLoading || !bulkAction}>
            {bulkLoading ? 'Applying…' : 'Apply'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected([])}>Cancel</button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {isStaff && (
                <th style={{ width:36 }}>
                  <input type="checkbox" checked={selected.length === tickets.length && tickets.length > 0}
                    onChange={toggleAll} style={{ cursor:'pointer' }} />
                </th>
              )}
              <th style={{ width:80 }}>ID</th>
              <th>Subject</th>
              <th>Category</th>
              <th>Branch</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={isStaff ? 10 : 9} style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>Loading…</td></tr>}
            {!loading && tickets.length === 0 && (
              <tr><td colSpan={isStaff ? 10 : 9}><div className="empty-state"><div className="empty-icon">🎫</div><div>No tickets found</div></div></td></tr>
            )}
            {tickets.map(t => (
              <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}
                style={{ background: selected.includes(t.id) ? 'var(--sky-light, #eff6ff)' : undefined }}>
                {isStaff && (
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(t.id)}
                      onChange={e => toggleSelect(t.id, e)} style={{ cursor:'pointer' }} />
                  </td>
                )}
                <td><span className="ticket-id">{t.ticket_number}</span></td>
                <td>
                  <div style={{ maxWidth:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontWeight:500, fontSize:12 }}>{t.subject}</div>
                  {t.sla_breached && <span style={{ fontSize:10, color:'var(--red)', fontWeight:600 }}>⚠️ SLA Breached</span>}
                </td>
                <td style={{ color:'var(--text3)', fontSize:11 }}>{t.category}</td>
                <td style={{ color:'var(--text3)', fontSize:11 }}>{t.branch}</td>
                <td><span className={`prio-dot ${PRIO_COLOR[t.priority]}`} />{t.priority}</td>
                <td><span className={`badge badge-${t.status}`}>{STATUS_LABELS[t.status]}</span></td>
                <td style={{ color:'var(--text3)', fontSize:11 }}>{t.assigned_to_name || '—'}</td>
                <td style={{ color:'var(--text3)', fontSize:11, whiteSpace:'nowrap' }}>{format(new Date(t.created_at), 'dd MMM, HH:mm')}</td>
                <td><button className="action-btn" onClick={e => { e.stopPropagation(); navigate(`/tickets/${t.id}`); }}>View →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > 15 && (
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
          <button className="btn btn-ghost btn-sm" disabled={filter.page===1} onClick={() => setFilter({ ...filter, page:filter.page-1 })}>← Prev</button>
          <span style={{ padding:'4px 10px', fontSize:12, color:'var(--text3)' }}>Page {filter.page} of {Math.ceil(total/15)}</span>
          <button className="btn btn-ghost btn-sm" disabled={filter.page>=Math.ceil(total/15)} onClick={() => setFilter({ ...filter, page:filter.page+1 })}>Next →</button>
        </div>
      )}
    </div>
  );
}
